import React, { useEffect, useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import classnames from 'classnames'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Paper from '@material-ui/core/Paper'
import Button from '@material-ui/core/Button'
import Avatar from '@material-ui/core/Avatar'
import Chip from '@material-ui/core/Chip'
import DoneIcon from '@material-ui/icons/Done'
import CloseIcon from '@material-ui/icons/Close'
import CardIcon from '@material-ui/icons/CreditCard'
import PdfIcon from '@material-ui/icons/PictureAsPdf'
import InfoIcon from '@material-ui/icons/Info'
import IconButton from '@material-ui/core/IconButton'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'

import Loading from 'components/system/Loading'
import Table from 'components/table/SimpleTable'
import TitleValueGrid from 'components/widgets/TitleValueGrid'
import Products from 'components/payments/Products'
import Window from 'components/dialog/Window'
import ConfirmSubscriptionChangeWindow from 'components/payments/ConfirmSubscriptionChangeWindow'

import priceUtils from 'utils/price'
import subscriptionUtils from 'utils/subscription'
import paymentSelectors from 'store/selectors/payment'
import paymentActions from 'store/modules/payment'
import routerSelectors from 'store/selectors/router'
import routerActions from 'store/modules/router'
import snackbarActions from 'store/modules/snackbar'

import settings, {
  CURRENCY,
} from 'settings'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
  loadingContainer: {
    width: '100%',
    paddingTop: '100px',
  },
  container: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  pricingToolbar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
  },
  pricingNote: {
    flexGrow: 0,
  },
  currencies: {
    textAlign: 'right',
    flexGrow: 1,
  },
  viewPlansButton: {
    width: '100%',
    textAlign: 'right',
    paddingRight: theme.spacing(2),
  },
  button: {
    textTransform: 'none',
    color: '#666'
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionButton: {
    marginTop: theme.spacing(2),
  },
  gridTitle: {
    textAlign: 'right',
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
  marginRight: {
    marginRight: theme.spacing(1),
  },
  smallText: {
    fontSize: '0.8em'
  },
  buttons: {
    marginTop: theme.spacing(4),
  },
  footer: {
    marginTop: theme.spacing(4),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
  },
  selectedCardTitle: {
    color: theme.palette.primary.main,
  },
  dedicatedCardActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    '& .button': {
      flexGrow: 1,
    },
    '& .buttonLeft': {
      textAlign: 'left',
    },
    '& .buttonRight': {
      textAlign: 'right',
    },
    '& .chip': {
      flexGrow: 0,
    }
  },
}))

const INVOICE_FIELDS = [{
  title: 'Date',
  name: 'date',
}, {
  title: 'Amount',
  name: 'amount',
}]

const CANCALLED_SUBSCRIPTION_FIELDS = [{
  title: 'Ended',
  name: 'ended',
}, {
  title: 'Monthly Total',
  name: 'total',
}]

const Payment = ({

}) => {
  const dispatch = useDispatch()
  const classes = useStyles()

  const [ cancelSubscriptionWindowOpen, setCancelSubscriptionWindowOpen ] = useState(false)
  const [ viewCancelledSubscription, setViewCancelledSubscription ] = useState(null)
  const [ editSubscrptionQuantities, setEditSubscrptionQuantities ] = useState(null)

  const loaded = useSelector(paymentSelectors.loaded)
  const sharedProducts = useSelector(paymentSelectors.sharedProducts)
  const dedicatedProducts = useSelector(paymentSelectors.dedicatedProducts)
  const sharedPlan = useSelector(paymentSelectors.sharedPlan)
  const subscription = useSelector(paymentSelectors.subscription)
  const invoices = useSelector(paymentSelectors.invoices)
  const card = useSelector(paymentSelectors.card)
  const productMap = useSelector(paymentSelectors.productMap)
  const subscriptionQuantities = useSelector(paymentSelectors.subscriptionQuantities)
  const subscriptionTable = useSelector(paymentSelectors.subscriptionTable)
  const subscriptionInfo = useSelector(paymentSelectors.subscriptionInfo)
  const cancelledSubscriptions = useSelector(paymentSelectors.cancelledSubscriptions)
  const cancelledSubscriptionInvoices = useSelector(paymentSelectors.cancelledSubscriptionInvoices)
  const params = useSelector(routerSelectors.params)
  const changePlanInfo = useSelector(paymentSelectors.changePlanInfo)

  const onCancelSubscription = useCallback(async () => {
    const result = await dispatch(paymentActions.cancelSubscription())
    if(result) {
      setCancelSubscriptionWindowOpen(false)
    }
  })

  const onChangeCard = useCallback(() => {
    dispatch(paymentActions.changeCard())
  })

  const invoiceTableData = useMemo(() => {
    return invoices.map(invoice => {
      return {
        id: invoice.id,
        date: new Date(invoice.created * 1000).toLocaleDateString(),
        amount: `${CURRENCY}${priceUtils.displayPrice(invoice.total)}`,
        invoice_pdf: invoice.invoice_pdf,
      }
    })
  }, [
    invoices,
  ])

  const cancelledSubscriptionTableData = useMemo(() => {
    return cancelledSubscriptions.map(sub => {
      const info = subscriptionUtils.getSubscriptionInfo(sub, productMap)
      return {
        id: sub.id,
        sub,
        ended: info.periodEnd,
        total: `${ CURRENCY }${ priceUtils.displayPrice(info.total) }`,
      }
    })
  }, [
    cancelledSubscriptions,
  ])

  const cancelledSubscriptionInvoiceTableData = useMemo(() => {
    return cancelledSubscriptionInvoices.map(invoice => {
      return {
        id: invoice.id,
        date: new Date(invoice.created * 1000).toLocaleDateString(),
        amount: `${ CURRENCY }${priceUtils.displayPrice(invoice.total)}`,
        invoice_pdf: invoice.invoice_pdf
      }
    })
  }, [
    cancelledSubscriptionInvoices,
  ])

  const getInvoiceActions = useCallback((item) => {
    return (
      <>
        <Button
          size="small"
          className={ classnames(classes.button) }
          variant="outlined"
          onClick={ () => {
            window.open(item.invoice_pdf)
          }}
        >
          <PdfIcon />&nbsp;&nbsp;Download
        </Button>
      </>
    )
  }, [
    classes,
  ])

  const getCancelledSubscriptionActions = useCallback((item) => {
    return (
      <>
        <Button
          size="small"
          className={ classnames(classes.button) }
          variant="outlined"
          onClick={ () => {
            setViewCancelledSubscription(item.sub)
          }}
        >
          <InfoIcon />&nbsp;&nbsp;View
        </Button>
      </>
    )
  }, [
    classes,
  ])

  const onEditDedicatedAmounts = useCallback(() => {
    setEditSubscrptionQuantities(subscriptionQuantities)
  }, [
    subscriptionQuantities
  ])

  const onChangeDedicatedAmount = useCallback((product, amount = 1) => {
    const existing = editSubscrptionQuantities[product] || 0
    const newValue = existing + amount
    if(newValue < 0) newValue = 0
    const newQuantities = Object.assign({}, editSubscrptionQuantities, {
      [product]: newValue,
    })
    setEditSubscrptionQuantities(newQuantities)
  }, [
    editSubscrptionQuantities
  ])

  const onConfirmEditSubscrptionQuantities = useCallback(async () => {
    const result = await dispatch(paymentActions.updateQuantities(editSubscrptionQuantities))
    if(result) setEditSubscrptionQuantities(null)
  }, [
    editSubscrptionQuantities
  ])

  const onCancelChangeSubscription = useCallback(() => {
    dispatch(paymentActions.setChangePlanInfo(null))
  }, [])

  const onConfirmChangeSubscription = useCallback(() => {
    dispatch(paymentActions.confirmChangePlan())
  }, [])

  useEffect(() => {
    dispatch(paymentActions.loadProducts())
    dispatch(paymentActions.loadStatus())
  }, [])

  useEffect(() => {
    if(!viewCancelledSubscription) {
      dispatch(paymentActions.setCancelledSubscriptionInvoices([]))
      return
    }
    dispatch(paymentActions.loadCancelledSubscriptionInvoices(viewCancelledSubscription.id))
  }, [
    viewCancelledSubscription,
  ])

  useEffect(() => {
    if(params.card_changed) {
      dispatch(routerActions.removeQueryParams({card_changed: true}))
      dispatch(snackbarActions.setSuccess('Your card is updating'))

      // wait 10 seconds for the card to ping the webhook then reload
      setTimeout(() => {
        dispatch(paymentActions.loadStatus())
      }, 10 * 1000)
    }
    else if(params.plan_changed) {
      dispatch(routerActions.removeQueryParams({plan_changed: true}))
      dispatch(snackbarActions.setSuccess('Your subscription has been updated'))
    }
  }, [
    params,
  ])

  if(!loaded) return (
    <div className={ classes.loadingContainer }>
      <Loading />
    </div>
  )
  
  return (
    <div className={ classes.root }>
      <Grid container spacing={ 2 }>
        <Grid item xs={ 6 }>
          <div className={ classes.container }>
            <Typography gutterBottom variant="h4">
              Plans
            </Typography>
            <Grid container spacing={ 2 }>
              <Grid item xs={ 6 }>
                <Typography gutterBottom variant="h6" className={ classes.sectionTitle }>
                  Developer
                </Typography>
                <Products
                  products={ sharedProducts }
                  getFeatures={ settings.PRODUCT_META_FIELDS.shared }
                  getCardTheme={ product => {
                    return sharedPlan && product.id == sharedPlan.id ? {
                      title: classes.selectedCardTitle,
                      subtitle: classes.selectedCardTitle,
                    } : {}
                  }}
                  getActions={ product => {
                    return sharedPlan && product.id == sharedPlan.id ? (
                      <Chip
                        avatar={<Avatar><DoneIcon /></Avatar>}
                        label="This is your current plan"
                        color="primary"
                      />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={ () => {
                          dispatch(paymentActions.changeSharedPlan(product))
                        }}
                      >
                        { subscription ? 'Change' : 'Select' } Plan
                      </Button>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={ 6 }>
                <Typography gutterBottom variant="h6" className={ classes.sectionTitle }>
                  Dedicated
                </Typography>
                <Products
                  products={ dedicatedProducts }
                  getFeatures={ settings.PRODUCT_META_FIELDS.dedicated }
                  getActions={ product => {
                    const amount = subscriptionQuantities[product.id] || 0
                    return (
                      <div className={ classes.dedicatedCardActions }>
                        <div className="button">
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={ onEditDedicatedAmounts }
                          >
                            Edit Amount
                          </Button>
                        </div>
                        <div className="chip">
                          <Chip color="primary" label={ amount } />
                        </div>
                        
                      </div>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid item xs={12} sm={5}>
        {
          subscription && card && (
            <>
              <Paper className={ classes.paper }>
                <TitleValueGrid
                  title="Subscription"
                  data={ subscriptionTable }
                />
                <div className={ classes.sectionButton }>
                  {
                    subscription.cancel_at_period_end ? (
                      <div>
                        <Typography gutterBottom>
                          This subscription is cancelled and we will take no more payments.
                        </Typography>
                        <Typography gutterBottom>
                          You can still use all the features you have paid for up until { subscriptionInfo.periodEnd }
                        </Typography>
                      </div>
                    ) : (
                      <Button
                        size="small"
                        className={ classes.button }
                        variant="outlined"
                        onClick={ () => setCancelSubscriptionWindowOpen(true) }
                      >
                        <CloseIcon />&nbsp;&nbsp;Cancel Subscription
                      </Button>
                    )
                  }
                  
                </div>
              </Paper>
              <Paper className={ classes.paper }>
                <TitleValueGrid
                  title="Card"
                  data={[{
                    title: 'Type',
                    value: card.card.brand,
                  },{
                    title: 'Expires',
                    value: `${card.card.exp_month} / ${card.card.exp_year}`,
                  },{
                    title: 'Last 4 Digits',
                    value: card.card.last4,
                  }]}
                />
                <div className={ classes.sectionButton }>
                  <Button
                    size="small"
                    className={ classes.button }
                    variant="outlined"
                    onClick={ onChangeCard }
                  >
                    <CardIcon />&nbsp;&nbsp;Change Card
                  </Button>
                </div>
              </Paper>
              <Paper className={ classes.paper }>
                <Grid container spacing={ 4 }>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Invoices
                    </Typography>
                  </Grid>
                </Grid>
                <Table
                  data={ invoiceTableData }
                  fields={ INVOICE_FIELDS }
                  getActions={ getInvoiceActions }
                />
              </Paper>
            </>
          )
        }
        {
          cancelledSubscriptions.length > 0 && (
            <Paper className={ classes.paper }>
              <Grid container spacing={ 4 }>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Previous Subscriptions
                  </Typography>
                </Grid>
              </Grid>
              <Table
                data={ cancelledSubscriptionTableData }
                fields={ CANCALLED_SUBSCRIPTION_FIELDS }
                getActions={ getCancelledSubscriptionActions }
              />
            </Paper>
          )
        }
      </Grid>
      </Grid>
      {
        cancelSubscriptionWindowOpen && (
          <Window
            open
            withCancel
            title="Cancel Subscription"
            cancelTitle="Close"
            submitTitle="I am sure - cancel subscription"
            size="sm"
            onSubmit={ onCancelSubscription }
            onCancel={ () => setCancelSubscriptionWindowOpen(false) }
          >
            <Typography gutterBottom>
              Are you <strong>absolutely sure</strong> you want to cancel your subscription?
            </Typography>
            <Typography gutterBottom>
              If you do, we will immediately stop all payments.
            </Typography>
            <Typography gutterBottom>
               You you have already paid until <strong>{ subscriptionInfo.periodEnd }</strong>, you can continue to use all features until then.
            </Typography>
            <Typography gutterBottom>
              After <strong>{ subscriptionInfo.periodEnd }</strong>, your plan will revert to "free".
            </Typography>
          </Window>
        )
      }
      {
        viewCancelledSubscription && (
          <Window
            open
            withCancel
            cancelTitle="Close"
            size="md"
            onCancel={ () => setViewCancelledSubscription(null) }
          >
            <TitleValueGrid
              title="Cancelled Subscription"
              data={ subscriptionUtils.getSubscriptionTable(viewCancelledSubscription, productMap) }
            />
            <Grid container spacing={ 4 } className={ classes.marginTop }>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Previous Invoices
                </Typography>
              </Grid>
            </Grid>
            <Table
              data={ cancelledSubscriptionInvoiceTableData }
              fields={ INVOICE_FIELDS }
              getActions={ getInvoiceActions }
            />
          </Window>
        )
      }
      {
        changePlanInfo && (
          <ConfirmSubscriptionChangeWindow
            changeInfo={ changePlanInfo }
            subscription={ subscription }
            onCancel={ onCancelChangeSubscription }
            onConfirm={ onConfirmChangeSubscription }
          />
        )
      }
      {
        editSubscrptionQuantities && (
          <Window
            open
            withCancel
            cancelTitle="Close"
            submitTitle="Update Subscription"
            size="xl"
            onCancel={ () => setEditSubscrptionQuantities(null) }
            onSubmit={ onConfirmEditSubscrptionQuantities }
          >
            <Products
              cellSize={ 3 }
              products={ dedicatedProducts }
              getFeatures={ settings.PRODUCT_META_FIELDS.dedicated }
              getActions={ product => {
                const amount = editSubscrptionQuantities[product.id] || 0
                return (
                  <div className={ classes.dedicatedCardActions }>
                    <div className="button buttonLeft">
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={ () => onChangeDedicatedAmount(product.id, -1) }
                      >
                        <RemoveIcon />
                      </Button>
                    </div>
                    <div className="chip">
                      <Chip color="primary" label={ amount } />
                    </div>
                    <div className="button buttonRight">
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={ () => onChangeDedicatedAmount(product.id, 1) }
                      >
                        <AddIcon />
                      </Button>
                    </div>
                    
                  </div>
                )
              }}
            />
          </Window>
        )
      }
    </div>
  )
}

export default Payment